<template>
  <v-dialog
    :value="isOpen"
    persistent
    :max-width="dialogMaxWidth"
    @input="onDialogInput"
  >
    <div
      class="pop-up"
      :class="{
        'pop-up--step-one': localStepNumber === 1 && !suggestPanelOpen,
        'pop-up--step-two': localStepNumber === 2 && !suggestPanelOpen,
        'pop-up--suggest': suggestPanelOpen,
      }"
    >
      <div class="modal_header">
        <v-btn
          icon
          class="modal_header__close"
          color="grey"
          aria-label="Close"
          @click="closeDialog"
        >
          <Icon class="cursor-pointer" name="popup_close" />
        </v-btn>
        <div class="modal_header__content">
          <div class="modal_header__icon-wrap" aria-hidden="true">
            <PayRangeEditorIcon />
          </div>
          <h3 class="modal_header__title">
            {{ modalTitle }}
          </h3>
        </div>
      </div>

      <!-- Step 1: Identity & scope -->
      <div v-show="localStepNumber === 1 && !suggestPanelOpen">
        <div class="step-one-container">
          <div v-if="effectiveDatesEnabled" class="modal_content step-one-effective-dates">
            <EffectiveDateToEffectiveTo
              :effective-from="effectiveFrom || ''"
              :effective-to="effectiveTo"
              @update:effectiveFrom="effectiveFrom = $event"
              @update:effectiveTo="effectiveTo = $event"
            />
          </div>

          <div class="modal_content">
            <label class="field-label" for="pay-range-job-family">Job Family*</label>
            <div class="pay-range-job-family-field">
              <div
                v-if="jobFamily?.levelId"
                class="pay-range-job-family-field__picked"
                role="button"
                tabindex="0"
                @click="openJobFamilyDialog"
                @keydown.enter="openJobFamilyDialog"
                @keydown.space.prevent="openJobFamilyDialog"
              >
                <JobFamilyIcon size="normal" class="pay-range-job-family-field__icon" />
                <div class="pay-range-job-family-field__breadcrumb">
                  <span class="pay-range-job-family-field__ancestor">{{
                    jobFamily.jobFamilyName
                  }}</span>
                  <span class="pay-range-job-family-field__sep">›</span>
                  <span class="pay-range-job-family-field__leaf">{{
                    formatJobFamilyLevelLabel(jobFamily.level)
                  }}</span>
                </div>
                <span v-if="jobFamily.code" class="pay-range-job-family-field__code"
                  >#{{ jobFamily.code }}</span
                >
                <div
                  class="pay-range-job-family-field__clear"
                  role="button"
                  tabindex="0"
                  aria-label="Clear job family selection"
                  @click.stop="handleJobFamilyClear"
                  @keydown.enter.stop="handleJobFamilyClear"
                  @keydown.space.stop.prevent="handleJobFamilyClear"
                >
                  <Icon name="x_close_icon" />
                </div>
              </div>
              <button
                v-else
                id="pay-range-job-family"
                type="button"
                class="pay-range-job-family-field__trigger"
                @click="openJobFamilyDialog"
              >
                <JobFamilyIcon size="normal" class="pay-range-job-family-field__trigger-icon" />
                <span class="pay-range-job-family-field__trigger-label"
                  >Select job family and level</span
                >
              </button>
            </div>
          </div>

          <div class="step-one-columns">
            <div class="step-one-column step-one-column--location">
              <div class="modal_content step-one-location-field">
                <label class="field-label" for="pay-range-location">Location</label>
                <LocationPicker
                  :key="`pay-range-location-${pickerSessionKey}`"
                  id="pay-range-location"
                  :model-value="payRangeLocation"
                  @update:model-value="updatePayRangeLocation"
                  @update:modelValue="updatePayRangeLocation"
                  :countries="locationCountryOptions || []"
                  :required="true"
                  :loading="locationPickerLoading"
                  :region-items="regionItems"
                  :sub-region-items="subRegionItems"
                  @search="onLocationSearch"
                  @clear="resetSearchResults"
                />
              </div>
            </div>

            <div class="step-one-column step-one-column--settings">
              <div class="modal_content">
                <CurrencyPicker
                  :key="`pay-range-currency-${pickerSessionKey}`"
                  pickerLabel="Currency"
                  label="Currency"
                  searchLabel="Select Currency"
                  listSource="operating"
                  :disable-scroll-pagination="true"
                  :items="currencyPickerItems"
                  @item-currency-selected="handleCurrencyChange"
                  v-model="selectedCurrency"
                  class="pay-range-currency-picker"
                  :class="{ 'changed-field': isFieldDirty('operatingCurrency') }"
                  @clear-selection="clearSelectedCurrency"
                  :isSelectedItemRemove="true"
                  :isforceReadonly="isSingleOperatingCurrency"
                />
                <p
                  v-if="isSingleOperatingCurrency"
                  class="pay-range-currency-helper"
                >
                  This account has only one operating currency
                </p>
              </div>

              <div class="modal_content">
                <ExSelectPicker
                  :class="[
                    'expressed-selector',
                    selectedPayBasis ? 'margin-left-zero' : '',
                    isFieldDirty('payBasis') ? 'changed-field' : '',
                  ]"
                  pickerLabel="Pay Basis"
                  label="Pay Basis"
                  searchLabel="Select Pay Basis"
                  :items="payBasisOptions"
                  v-model="selectedPayBasis"
                  @item-selected="handlePayBasisChange"
                  @clear-selection="clearPayBasis"
                  :isSelectedItemRemove="true"
                  :hasImageItem="false"
                />
              </div>

              <div class="modal_content">
                <ExSelectPicker
                  :class="[
                    'expressed-selector',
                    selectedWorkMode ? 'margin-left-zero' : '',
                    isFieldDirty('workMode') ? 'changed-field' : '',
                  ]"
                  pickerLabel="Work"
                  label="Work"
                  searchLabel="Select Work"
                  :items="workModeOptions"
                  v-model="selectedWorkMode"
                  @item-selected="handleWorkModeChange"
                  @clear-selection="clearWorkMode"
                  :isSelectedItemRemove="false"
                  :hasImageItem="false"
                  :show-required-mark="false"
                />
              </div>
            </div>
          </div>

          <div class="step-one-footer">
            <div class="step-indicator" :aria-label="`Step ${localStepNumber} of 2`">
              <span class="step-indicator__label">Step {{ localStepNumber }} of 2</span>
              <v-icon size="24px" class="step-indicator__icon">
                {{ localStepNumber === 1 ? "$exRadioCurrentCheck" : "$exRadioChecked" }}
              </v-icon>
              <v-icon size="24px" class="step-indicator__icon">
                {{ localStepNumber === 1 ? "$exRadioCheckOff" : "$exRadioCurrentCheck" }}
              </v-icon>
            </div>
            <div class="step-one-footer__actions">
              <AskIQButton
                v-if="suggestRangeEnabled"
                class="step-one-suggest-btn"
                label="Suggest Range"
                variant="light"
                :height="40"
                :disabled="stepOneButtonsDisabled"
                @click="openSuggestPanel"
              />
              <ExButton
                label="Enter Range"
                class="step-one-btn"
                size="normal"
                buttonWidth="auto"
                :normal="stepOneButtonsDisabled ? 'normal-disable' : 'normal-hover'"
                @onClick="goToStepTwo"
                :disabled="stepOneButtonsDisabled"
                rightIcon="right-icon.svg"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Compensation (readiness rail) -->
      <div v-show="localStepNumber === 2 && !suggestPanelOpen" class="step-two-container">
        <div v-if="!hasStepOneContext" class="step-two-invalid" role="status">
          <span class="step-two-invalid__title">Step 1 is incomplete</span>
          <span class="step-two-invalid__message">
            Go back and select a Job Family and Location before entering a range.
          </span>
          <ExButton
            class="footer-btn step-two-invalid__btn"
            size="normal"
            outline="outline-hover"
            icon="arrow-icon.svg"
            label="Back to Step 1"
            buttonWidth="auto"
            @onClick="goBack"
          />
        </div>

        <template v-else>
          <div class="step-two-header">
            <div class="step-two-header__line">
              <JobFamilyIcon class="step-two-header__icon" />
              <span class="step-two-header__breadcrumb">{{ jobFamilyBreadcrumb }}</span>
              <span v-if="stepTwoMetaLabel" class="step-two-header__meta">
                {{ stepTwoMetaLabel }}
              </span>
            </div>
            <div class="step-two-header__location">{{ payRangeLocationLabel }}</div>
            <span class="secondary-text">Values are for Full Time (1 FTE)</span>
          </div>

          <div class="step-two-body">
            <PayRangeEditor
              :isEditable="true"
              :topRange="topRange"
              :bottomRange="bottomRange"
              :unitSuffix="unitSuffix"
              :showEmptyError="showRangeEmptyError"
              @topRangeUpdated="handleTopRangeChange"
              @bottomRangeUpdated="handleBottomRangeChange"
            />
          </div>

          <div class="step-two-footer">
            <div class="step-indicator" :aria-label="`Step ${localStepNumber} of 2`">
              <span class="step-indicator__label">Step {{ localStepNumber }} of 2</span>
              <v-icon size="24px" class="step-indicator__icon">
                {{ localStepNumber === 1 ? "$exRadioCurrentCheck" : "$exRadioChecked" }}
              </v-icon>
              <v-icon size="24px" class="step-indicator__icon">
                {{ localStepNumber === 1 ? "$exRadioCheckOff" : "$exRadioCurrentCheck" }}
              </v-icon>
            </div>
            <div class="step-two-footer__actions">
              <ExButton
                class="footer-btn"
                size="normal"
                outline="outline-hover"
                icon="arrow-icon.svg"
                label="Back"
                buttonWidth="auto"
                @onClick="goBack"
              />
              <ExButton
                label="Save Pay Range as Draft"
                class="footer-btn step-two-btn"
                size="normal"
                buttonWidth="auto"
                :normal="!canSaveDraft ? 'normal-disable' : 'normal-hover'"
                :loading="internalSaving ? 'right' : null"
                @onClick="saveAsDraft"
                :disabled="!canSaveDraft"
              />
            </div>
          </div>
          <p v-if="saveError" class="save-error">{{ saveError }}</p>
        </template>
      </div>

      <!-- Suggest Range panel -->
      <div v-show="suggestPanelOpen" class="suggest-container">
        <div class="suggest-header">
          <div class="suggest-header__icon-row" aria-hidden="true">
            <Icon name="iq_small_icon" class="suggest-iq-badge" />
            <JobFamilyIcon class="suggest-header__family-icon" />
          </div>
          <p class="suggest-header__primary">{{ suggestHeaderPrimaryLine }}</p>
          <p v-if="suggestHeaderSecondaryLine" class="suggest-header__secondary">
            {{ suggestHeaderSecondaryLine }}
          </p>
        </div>

        <div
          class="suggest-body"
          :class="{ 'suggest-body--loading': suggestLoading }"
        >
          <div
            v-if="suggestLoading"
            class="suggest-thinking"
            aria-live="polite"
            aria-busy="true"
          >
            <IQThinkingBanner
              state="thinking"
              variant="quiz"
              :title="suggestIsConnecting ? 'Researching …' : ''"
              :caption="suggestIsConnecting ? 'Sourcing Pay Range sources' : ''"
              :reasoning="suggestReasoning"
              :phase="suggestPhase"
              :stalled="suggestStalled"
              :started-at="suggestStartedAt"
              collapsible
              :use-store-reasoning="false"
            />
          </div>

          <div v-else-if="suggestState === 'sourced'" class="suggest-left">
            <span class="secondary-text suggest-left-caption">Values are for Full Time (1 FTE)</span>
            <PayRangeEditor
              :isEditable="true"
              :topRange="suggestMax"
              :bottomRange="suggestMin"
              :unitSuffix="unitSuffix"
              @topRangeUpdated="handleSuggestMaxChange"
              @bottomRangeUpdated="handleSuggestMinChange"
            />
          </div>

          <div
            v-if="!suggestLoading"
            class="suggest-right"
            :class="{ 'suggest-right--full': isSuggestFailure }"
          >
            <template v-if="suggestState === 'sourced' && suggestSources.length">
              <IQThinkingBanner
                class="suggest-done-banner"
                state="done"
                variant="quiz"
                :reasoning="suggestReasoning"
                :done-elapsed-ms="suggestElapsedMs"
                :started-at="suggestStartedAt"
                collapsible
                :use-store-reasoning="false"
              />
              <section class="market-data">
                <h4 class="market-data-title">
                  MARKET DATA — {{ suggestSources.length }} SOURCES CONSULTED
                </h4>
                <table class="market-data-table">
                  <thead>
                    <tr>
                      <th scope="col">Source</th>
                      <th scope="col">Min</th>
                      <th scope="col">Median</th>
                      <th scope="col">Max</th>
                      <th scope="col">Data as of</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="source in suggestSources"
                      :key="source.name"
                      :class="{ 'source--excluded': isExcludedSource(source) }"
                    >
                      <td class="source-cell">
                        <a
                          v-if="source.url"
                          :href="source.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="source-link"
                        >
                          {{ source.name }}
                        </a>
                        <span v-else>{{ source.name }}</span>
                      </td>
                      <td>{{ formatSourceAmount(source.min) }}</td>
                      <td>{{ formatSourceAmount(source.median) }}</td>
                      <td>{{ formatSourceAmount(source.max) }}</td>
                      <td>{{ source.data_as_of || "—" }}</td>
                      <td>
                        <span v-if="isExcludedSource(source)" class="outlier-chip">Outlier</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section v-if="hasSuggestion" class="triangulated">
                <h4 class="market-data-title">TRIANGULATED SUGGESTION</h4>
                <div class="suggestion-boxes">
                  <div class="suggestion-box">
                    <span class="suggestion-label">Suggested Min</span>
                    <span class="suggestion-value">{{ formatSourceAmount(suggestion.min) }}</span>
                  </div>
                  <div
                    v-if="suggestion.median != null"
                    class="suggestion-box suggestion-box--muted"
                  >
                    <span class="suggestion-label">Median</span>
                    <span class="suggestion-value">{{ formatSourceAmount(suggestion.median) }}</span>
                  </div>
                  <div class="suggestion-box">
                    <span class="suggestion-label">Suggested Max</span>
                    <span class="suggestion-value">{{ formatSourceAmount(suggestion.max) }}</span>
                  </div>
                </div>
                <p class="suggestion-rationale">
                  Median-of-medians across non-excluded sources. Median shown for
                  reference only — not saved.
                </p>
              </section>

              <div v-if="excludedSources.length" class="exclusion-banner" role="alert">
                <span
                  v-for="source in excludedSources"
                  :key="source.name"
                  class="exclusion-item"
                >
                  {{ source.name }} excluded — {{ source.exclusion_reason || "outside the expected range." }}
                </span>
              </div>
            </template>

            <IQThinkingBanner
              v-else-if="isSuggestFailure"
              state="failed"
              :failedTitle="suggestFailureTitle"
              :failedMessage="suggestFailureMessage"
              :hideRetry="true"
            />
          </div>
        </div>

        <div class="suggest-footer step-two-footer">
          <ExButton
            class="footer-btn"
            size="normal"
            outline="outline-hover"
            icon="arrow-icon.svg"
            label="Back"
            buttonWidth="auto"
            @onClick="closeSuggestPanel"
          />
          <div v-if="!suggestLoading" class="step-indicator" aria-label="Step 2 of 2">
            <span class="step-indicator__label">Step 2 of 2</span>
            <v-icon size="24px" class="step-indicator__icon">$exRadioChecked</v-icon>
            <v-icon size="24px" class="step-indicator__icon">$exRadioCurrentCheck</v-icon>
          </div>
          <div class="step-two-footer__actions">
          <template v-if="isSuggestFailure">
            <ExButton
              label="Try Again"
              class="footer-btn suggest-try-again-btn"
              size="normal"
              secondary
              buttonWidth="auto"
              @onClick="retrySuggest"
            />
            <ExButton
              label="Enter Range Manually"
              class="footer-btn step-two-btn"
              size="normal"
              buttonWidth="auto"
              :normal="'normal-hover'"
              @onClick="enterRangeManually"
            />
          </template>
          <ExButton
            v-else-if="!suggestLoading"
            label="Save Pay Range as Draft"
            class="footer-btn step-two-btn"
            size="normal"
            buttonWidth="auto"
            :normal="!canSaveSuggest ? 'normal-disable' : 'normal-hover'"
            :loading="internalSaving ? 'right' : null"
            @onClick="saveSuggestedRange"
            :disabled="!canSaveSuggest"
          />
          </div>
        </div>
        <p v-if="saveError && !suggestPanelOpen" class="save-error">{{ saveError }}</p>
      </div>
    </div>

    <JobFamilyPickerDialog
      :open="jobFamilyDialogOpen"
      :model-value="jobFamily"
      :results="jobFamilyResults"
      :loading="jobFamilyLoading"
      :default-expanded="true"
      :visible-row-count="5"
      :stack-above-modal="true"
      title="Select Job Family"
      subtitle="Choose the job family and level for this pay range."
      confirm-label="Select"
      busy-confirm-label="Selecting..."
      @update:open="jobFamilyDialogOpen = $event"
      @search="searchJobFamilies"
      @assign="handleJobFamilyPickerAssign"
      @cancel="jobFamilyDialogOpen = false"
    />
  </v-dialog>
</template>

<script setup>
import dayjs from "dayjs";
import { ref, computed, watch, getCurrentInstance, onMounted } from "vue";
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import CurrencyPicker from "@components/CurrencyPicker/CurrencyPicker.vue"; // alias is general import standard in project
import PayRangeEditorIcon from "@components/svgs/PayRangeEditorIcon.vue";
import ExButton from "@components/buttons/Button.vue";
import AskIQButton from "@components/AskIQButton/AskIQButton.vue";
import Icon from "@components/icons/Icon.vue";
import JobFamilyPickerDialog from "@components/JobFamilyPickerDialog/JobFamilyPickerDialog.vue";
import JobFamilyIcon from "@components/icons/items/OrgObject/JobFamilyIcon.vue";
import { formatLevelLabel as formatJobFamilyLevelLabel } from "@components/JobFamilyPickerDialog/jobFamilyPickerDialogUtils";
import LocationPicker from "@components/LocationPicker/LocationPicker.vue";
import PayRangeEditor from "@components/PayRange/PayRangeEditor.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import EffectiveDateToEffectiveTo from "@components/EffectiveDateToEffectiveTo/EffectiveDateToEffectiveTo.vue";
import {
  normalizeOperatingCurrencyForModal, 
  buildLocationPath,
  isPayRangeLocationComplete,
  resolveOperatingCountryId,
  resolveOperatingCurrencyId,
  toApiDateString,
  toSuggestApiRequest,
} from "@components/PayRangeModal/payRangeDraftPayload";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import { resolveCountryFlagIcon } from "@components/utils/countryFlagIcon";
import { resolveCurrencyForCountry } from "@components/utils/resolveCurrencyForCountry";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import { loadJobFamilyPickerResults } from "@components/JobFamilyPickerDialog/loadJobFamilyPickerResults";
import { usePayRangeLocationSearch } from "@components/PayRangeModal/composables/usePayRangeLocationSearch";
import { usePayRangeCountryCatalog } from "@components/PayRangeModal/composables/usePayRangeCountryCatalog";

const WORK_MODE_OPTIONS = [
  { id: 1, name: "On-Site", code: "ON_SITE" },
  { id: 2, name: "Hybrid", code: "HYBRID" },
  { id: 3, name: "Remote", code: "REMOTE" },
  { id: 4, name: "Unspecified", code: "UNSPECIFIED" },
];

const PAY_BASIS_OPTIONS = [
  { id: 1, name: "Hourly", code: "HOURLY", headerKey: "hour", unit: "Hour" },
  { id: 2, name: "Monthly", code: "MONTHLY", headerKey: "month", unit: "Month" },
  { id: 3, name: "Annually", code: "ANNUAL", headerKey: "year", unit: "Year" },
];

/**
 * Do NOT hardcode OperatingCountry / OperatingCurrency PKs here.
 * Account-scoped FKs differ per tenant (ER-10654: id:1 caused create-draft 404).
 * Create mode starts empty; user must pick from the operating-* list APIs.
 */
const DEFAULT_CURRENCY = null;

const props = defineProps({
  /** @deprecated Prefer `open` */
  showDialog: { type: Boolean, default: undefined },
  open: { type: Boolean, default: undefined },
  /** @deprecated Prefer `mode` */
  isEdit: { type: Boolean, default: false },
  mode: {
    type: String,
    default: null,
    validator: (v) => v === null || ["create", "edit"].includes(v),
  },
  stepNumber: { type: Number, default: 1 },
  payRangeId: { type: String, default: null },
  initialValues: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Optional async save handler. When provided, modal awaits it and closes on success.
   * Signature: (formPayload) => Promise<any>
   */
  saveDraft: { type: Function, default: null },
  /**
   * Optional async market-data handler for the Suggest Range panel.
   * Signature: (seed) => Promise<{ data: { sources: Array, suggestion: Object } }>
   */
  suggestRange: { type: Function, default: null },
  /** External saving lock from parent (optional) */
  saving: { type: Boolean, default: false },
  /** Account operating countries for Location (same scope as `currencyItems`). */
  countryItems: { type: Array, default: () => [] },
  /** Account operating currencies — picker list + id merge + single-currency UX. */
  currencyItems: { type: Array, default: () => [] },
  /** Optional picker list when `currencyItems` is empty (e.g. Storybook). */
  currencyCatalogItems: { type: Array, default: () => [] },
  /** Optional static job family list (Storybook). Empty → API search via dialog. */
  jobFamilyItems: { type: Array, default: () => [] },
});

const emit = defineEmits([
  "closeAddDialog",
  "onClose",
  "onSaveDraft",
  "onSuggestRange",
  "onAddJobFamily",
  "onLocationSearch",
  "update:open",
]);

const app = getCurrentInstance();
const { proxy } = app || {};
const isOpen = computed(() => {
  if (props.open !== undefined) return props.open;
  if (props.showDialog !== undefined) return props.showDialog;
  return false;
});

const resolvedMode = computed(() => {
  if (props.mode) return props.mode;
  return props.isEdit ? "edit" : "create";
});

const modalTitle = computed(() => "Add Pay Range");

const workModeOptions = ref(WORK_MODE_OPTIONS);
const payBasisOptions = ref(PAY_BASIS_OPTIONS);

const topRange = ref(null);
const bottomRange = ref(null);
const localStepNumber = ref(props.stepNumber || 1);
/** Source records (JSON array) persisted with a suggest save; preserved on edit re-save. */
const sources = ref(null);
const showRangeEmptyError = ref(false);
const isSaving = ref(false);
const saveError = ref("");
const internalSaving = computed(() => props.saving || isSaving.value);

const dirtyFields = ref({});

const selectedCurrency = ref(DEFAULT_CURRENCY);
/** Rule 34: true once the user picked/cleared Currency (or an existing draft supplied one) —
 *  a country change then never re-defaults it. */
const currencyTouched = ref(false);
/** ER-11873: exactly one active Operating Currency → lock + auto-default the Used Currency
 *  field. Detection lives in PayRangeModal (Eric's 2026-09-26 decision), driven by the active
 *  currency rows threaded down from FairPayPage. */
const isSingleOperatingCurrency = computed(
  () => (props.currencyItems || []).length === 1
);
/** Remount country/currency pickers on each create open so ExSelectPicker UI cannot keep stale chips. */
const pickerSessionKey = ref(0);
const selectedWorkMode = ref(
  WORK_MODE_OPTIONS.find((o) => o.code === "UNSPECIFIED")
);
const selectedPayBasis = ref(
  PAY_BASIS_OPTIONS.find((o) => o.code === "ANNUAL")
);

// --- Job Family + Location (ER-11729 Step 1 rework) -------------------------
/** Picked Job Family + Level — `{ jobFamilyId, jobFamilyName, code, levelId, level }`. */
const jobFamily = ref(null);
/** Job family search results (families with levels) for JobFamilyPickerDialog. */
const jobFamilyResults = ref([]);
const jobFamilyLoading = ref(false);
const jobFamilyQuery = ref("");
const jobFamilyDialogOpen = ref(false);
/** LocationPicker model — `{ countryCode, scope, regionId, subRegionType, subRegionId }`. */
const payRangeLocation = ref(null);

const {
  regionItems,
  subRegionItems,
  locationSearchLoading,
  handleLocationSearch,
  hydrateLocationFromRefs,
  ensureLocationForSuggest,
  resetSearchResults,
} = usePayRangeLocationSearch({
  getLocation: () => payRangeLocation.value,
  setLocation: (loc) => {
    payRangeLocation.value = enrichPayRangeLocation(loc);
  },
});

const { catalogLoading, loadCatalog, locationCountryOptions } = usePayRangeCountryCatalog(
  () => props.countryItems || [],
  getAccountId
);

const locationPickerLoading = computed(
  () => locationSearchLoading.value || catalogLoading.value
);

/** Full currency catalog for the picker (static list — no scroll pagination). */
function mapCurrencyRowToPickerItem(row) {
  if (!row) return null;
  const code = row.code || row.currency_code;
  if (!code) return null;
  return {
    id: row.id ?? row.operating_currency_id ?? row.catalogCurrencyId,
    code,
    name: row.name || code,
    operating_currency_id: row.operating_currency_id ?? row.id ?? null,
    icon: resolveCountryFlagIcon({
      code: row.iso_country_code || code,
      flag_icon: row.flag_icon || row.icon,
    }),
  };
}

const currencyPickerItems = computed(() => {
  const source =
    (props.currencyItems?.length ? props.currencyItems : null) ||
    (props.currencyCatalogItems?.length ? props.currencyCatalogItems : null) ||
    [];
  return source
    .map(mapCurrencyRowToPickerItem)
    .filter(Boolean)
    .sort((a, b) =>
      String(a.name || a.code).localeCompare(String(b.name || b.code))
    );
});

const enrichOperatingCurrency = (currency) => {
  if (!currency) return null;
  const normalized = normalizeOperatingCurrencyForModal(currency, { countryFlag: "" });
  const operatingCurrencyId = resolveOperatingCurrencyId(
    normalized,
    props.currencyItems || []
  );
  if (operatingCurrencyId == null) return normalized;
  if (
    normalized.id === operatingCurrencyId &&
    normalized.operating_currency_id === operatingCurrencyId
  ) {
    return normalized;
  }
  return {
    ...normalized,
    id: operatingCurrencyId,
    operating_currency_id: operatingCurrencyId,
  };
};

const enrichPayRangeLocation = (location) => {
  if (!location) return null;
  const operatingCountryId = resolveOperatingCountryId(
    location,
    locationCountryOptions.value || [],
    props.countryItems || []
  );
  if (operatingCountryId == null || location.operatingCountryId === operatingCountryId) {
    return location;
  }
  return { ...location, operatingCountryId };
};

// --- Suggest Range panel state -------------------------------------------
/** LaunchDarkly rollout gate — hidden until the flag is enabled (EC Rule 11). */
const suggestRangeEnabled = ref(false);
/** Same Fair Pay MVP flag (idea-MDP-429) gates the Effective from / to control on Step 1. */
const effectiveDatesEnabled = ref(false);
const todayDateString = () => dayjs().format("YYYY-MM-DD");
const effectiveFrom = ref(todayDateString());
const effectiveTo = ref(null);
const suggestPanelOpen = ref(false);
const suggestLoading = ref(false);
/**
 * Suggest-panel machine state:
 * "sourcing" | "sourced" | "cannot-source" | "timed-out" | "unavailable".
 * Selected from the backend's machine-readable reason, never a client guess.
 */
const suggestState = ref("sourcing");
const suggestSources = ref([]);
const suggestion = ref({ min: null, median: null, max: null });
const suggestMin = ref(null);
const suggestMax = ref(null);
/** Streamed AI thinking lifecycle (Streamed AI Thinking Standard). */
const suggestReasoning = ref("");
const suggestPhase = ref("");
const suggestStalled = ref(false);
const suggestStartedAt = ref(null);
const suggestFinishedAt = ref(null);
/**
 * Aborts the in-flight suggest stream. `suggestSessionToken` invalidates late
 * callbacks/results from an aborted session so Try Again can restart cleanly.
 */
let suggestAbortController = null;
let suggestSessionToken = 0;

const suggestIsConnecting = computed(
  () => !suggestPhase.value && !suggestReasoning.value
);

const suggestElapsedMs = computed(() => {
  if (suggestStartedAt.value == null) return null;
  const finished = suggestFinishedAt.value ?? Date.now();
  return Math.max(0, finished - suggestStartedAt.value);
});

const abortSuggestStream = () => {
  suggestSessionToken += 1;
  if (suggestAbortController) {
    suggestAbortController.abort();
    suggestAbortController = null;
  }
};

const isSuggestAbortError = (err) =>
  err?.name === "AbortError" ||
  err?.name === "CanceledError" ||
  err?.code === "ERR_CANCELED";

const resetSuggestState = () => {
  abortSuggestStream();
  suggestPanelOpen.value = false;
  suggestLoading.value = false;
  suggestState.value = "sourcing";
  suggestSources.value = [];
  suggestion.value = { min: null, median: null, max: null };
  suggestMin.value = null;
  suggestMax.value = null;
  suggestErrorBanner.value = null;
  suggestReasoning.value = "";
  suggestPhase.value = "";
  suggestStalled.value = false;
  suggestStartedAt.value = null;
  suggestFinishedAt.value = null;
};

const resetForm = () => {
  topRange.value = null;
  bottomRange.value = null;
  localStepNumber.value = 1;
  sources.value = null;
  saveError.value = "";
  showRangeEmptyError.value = false;
  dirtyFields.value = {};
  selectedCurrency.value = DEFAULT_CURRENCY;
  currencyTouched.value = false;
  selectedWorkMode.value = findWorkMode("UNSPECIFIED");
  selectedPayBasis.value = findPayBasis("ANNUAL");
  effectiveFrom.value = todayDateString();
  effectiveTo.value = null;
  jobFamily.value = null;
  jobFamilyResults.value = [];
  jobFamilyLoading.value = false;
  jobFamilyQuery.value = "";
  jobFamilyDialogOpen.value = false;
  payRangeLocation.value = null;
  resetSearchResults();
  resetSuggestState();
};

const findWorkMode = (code) =>
  WORK_MODE_OPTIONS.find((o) => o.code === code) ||
  WORK_MODE_OPTIONS.find((o) => o.code === "UNSPECIFIED");

const findPayBasis = (code) => {
  const mapped = {
    HOURLY: "HOURLY",
    MONTHLY: "MONTHLY",
    ANNUAL: "ANNUAL",
    per_hour: "HOURLY",
    per_month: "MONTHLY",
    per_year: "ANNUAL",
  };
  const key = mapped[code] || "ANNUAL";
  return PAY_BASIS_OPTIONS.find((o) => o.code === key);
};

const applyInitialValues = (values = {}) => {
  if (values.operatingCurrency) {
    selectedCurrency.value = enrichOperatingCurrency(values.operatingCurrency);
    currencyTouched.value = true;
  }
  if (values.workMode) selectedWorkMode.value = findWorkMode(values.workMode);
  if (values.payBasis) selectedPayBasis.value = findPayBasis(values.payBasis);
  if (values.minAmount != null) bottomRange.value = values.minAmount;
  if (values.maxAmount != null) topRange.value = values.maxAmount;
  if (values.sources != null) sources.value = values.sources;
  if (values.effectiveFrom) effectiveFrom.value = values.effectiveFrom;
  if (values.effectiveFrom || values.effectiveTo) {
    effectiveTo.value = values.effectiveTo ?? null;
  }

  // ER-11729: Job Family + Location replace the old name/region fields.
  if (values.jobFamily) {
    jobFamily.value = values.jobFamily;
    // Seed the picker's result list so the picked breadcrumb can resolve the
    // family + level names from the draft's own family (which carries `levels`).
    if (values.jobFamily.levels?.length) {
      jobFamilyResults.value = [values.jobFamily];
    }
  }
  if (values.jobFamilyLevelId != null && jobFamily.value?.levelId == null) {
    jobFamily.value = { ...(jobFamily.value || {}), levelId: values.jobFamilyLevelId };
  }
  if (values.location) {
    payRangeLocation.value = values.location;
    hydrateLocationFromRefs(payRangeLocation.value);
  }
};

/** ER-11873: when the account has exactly one active Operating Currency, pre-fill the Used
 *  Currency field with it — unless a currency is already selected (edit-draft hydrate). */
const applySingleCurrencyDefault = () => {
  if (!isSingleOperatingCurrency.value) return;
  const sole = (props.currencyItems || [])[0];
  if (!sole) return;
  if (selectedCurrency.value && selectedCurrency.value.id != null) return;
  selectedCurrency.value = enrichOperatingCurrency(sole);
};

watch(
  () => props.initialValues,
  (vals) => {
    if (vals && Object.keys(vals).length) {
      applyInitialValues(vals);
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => isOpen.value,
  (open) => {
    if (!open) {
      // Parent-driven close (v-dialog / update:open) also aborts sourcing.
      abortSuggestStream();
      return;
    }
    loadCatalog();
    saveError.value = "";
    isSaving.value = false;
    // Remount country/currency pickers on every open so ExSelectPicker shows icons/chips.
    pickerSessionKey.value += 1;
    resetForm();
    if (props.initialValues && Object.keys(props.initialValues).length) {
      applyInitialValues(props.initialValues);
    }
    applySingleCurrencyDefault();
    if (resolvedMode.value === "edit" && props.stepNumber) {
      localStepNumber.value = props.stepNumber;
    }
    // ER-11729: the Step 2 error fill renders on arrival, not on blur/save —
    // including when an edit draft reopens directly on Step 2.
    if (localStepNumber.value === 2) {
      showRangeEmptyError.value =
        topRange.value == null ||
        topRange.value === "" ||
        bottomRange.value == null ||
        bottomRange.value === "";
    }
  }
);

watch(
  () => props.stepNumber,
  (step) => {
    if (step) localStepNumber.value = step;
  }
);

// ER-11873: the active currency rows can arrive after the wizard is already open (FairPayPage
// fetches them on mount). Re-apply the single-currency default once they land. `immediate` also
// covers the case where the modal mounts directly open with a populated list.
watch(
  () => props.currencyItems,
  () => {
    if (isOpen.value) applySingleCurrencyDefault();
    if (selectedCurrency.value) {
      selectedCurrency.value = enrichOperatingCurrency(selectedCurrency.value);
    }
  },
  { immediate: true }
);

watch(
  () => [locationCountryOptions.value, props.countryItems],
  () => {
    if (!payRangeLocation.value) return;
    const enriched = enrichPayRangeLocation(payRangeLocation.value);
    if (enriched !== payRangeLocation.value) {
      payRangeLocation.value = enriched;
    }
  },
  { deep: true }
);

const markDirty = (field) => {
  dirtyFields.value = { ...dirtyFields.value, [field]: true };
};

const isFieldDirty = (field) =>
  Boolean(dirtyFields.value[field] && resolvedMode.value === "edit");

const unitSuffix = computed(() => {
  const code = selectedCurrency.value?.code || "USD";
  const unit = selectedPayBasis.value?.unit || "Year";
  return `${code}/${unit}`;
});

const payBasisHeaderKey = computed(
  () => selectedPayBasis.value?.headerKey || "year"
);

const jobFamilyLevelId = computed(() => jobFamily.value?.levelId ?? null);

/** "Engineering › 3 · Senior Engineer" — header / suggest breadcrumb. */
const jobFamilyBreadcrumb = computed(() => {
  const family = jobFamily.value;
  if (!family?.jobFamilyName) return "";
  const level = family.level;
  const parts = [family.jobFamilyName];
  if (level) parts.push(`${level.ordinal ?? ""} · ${level.name ?? ""}`);
  return parts.filter((p) => String(p).trim() !== "").join(" › ");
});

/**
 * Location line for the Step 2 header — the resolved path at whatever depth
 * LocationPicker reached (e.g. "United States • Colorado • Metro Denver"). Missing
 * levels are dropped so there are never empty "•" separators (AC 11).
 */
const payRangeLocationLabel = computed(() => {
  const path = buildLocationPath(payRangeLocation.value, locationCountryOptions.value);
  return path.length ? path.join(" • ") : "Country-wide";
});

/**
 * Step 2 header meta line: "On-Site • Paid in United States Dollar (USD) • Annually".
 * Built from whatever is actually selected so a missing segment never leaves a
 * dangling "•" separator (AC 11).
 */
const stepTwoMetaLabel = computed(() => {
  const parts = [];
  if (selectedWorkMode.value?.name) parts.push(selectedWorkMode.value.name);
  if (selectedCurrency.value?.name) {
    parts.push(
      `Paid in ${selectedCurrency.value.name} (${selectedCurrency.value.code || ""})`
    );
  }
  if (selectedPayBasis.value?.name) parts.push(selectedPayBasis.value.name);
  return parts.join(" • ");
});

/**
 * Step 2 identity — a resolved Job Family Level + Location. Without these the
 * readiness rail has nothing to describe, so Step 2 shows an invalid/empty state
 * instead of a broken editor (e.g. `stepNumber=2` opened with no draft data).
 */
const hasStepOneContext = computed(
  () =>
    jobFamilyLevelId.value != null &&
    isPayRangeLocationComplete(
      payRangeLocation.value,
      locationCountryOptions.value || []
    )
);

/** ER-12589: Effective from is required; Effective to (optional) must be on or after it. */
const isEffectivePeriodValid = computed(() => {
  if (!effectiveDatesEnabled.value) return true;
  const from = toApiDateString(effectiveFrom.value);
  if (!from) return false;
  const to = toApiDateString(effectiveTo.value);
  return !to || to >= from;
});

const isStepOneValid = computed(() => {
  // ER-11729: a pay range is identified by a Job Family Level + a Location.
  const hasJobFamilyLevel = jobFamilyLevelId.value != null;
  const hasLocation = isPayRangeLocationComplete(
    payRangeLocation.value,
    locationCountryOptions.value || []
  );
  const isPayBasisSelected = Boolean(
    selectedPayBasis.value?.code ?? selectedPayBasis.value
  );
  const currencyCode = String(
    selectedCurrency.value?.code || selectedCurrency.value?.currency_code || ""
  ).trim();
  const hasOperatingCurrency = currencyCode.length > 0;

  // Step 1: job family level + location + pay basis + used currency (Figma Step=1).
  return (
    hasJobFamilyLevel &&
    hasLocation &&
    isPayBasisSelected &&
    hasOperatingCurrency &&
    isEffectivePeriodValid.value
  );
});

const stepOneButtonsDisabled = computed(() => !isStepOneValid.value);

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

const handleJobFamilyUpdate = (value) => {
  if (!value) {
    jobFamily.value = null;
    markDirty("jobFamily");
    return;
  }
  const family = jobFamilyResults.value.find(
    (f) => f.jobFamilyId === value.jobFamilyId
  );
  const level = family?.levels?.find((l) => l.id === value.levelId) || null;
  jobFamily.value = {
    jobFamilyId: value.jobFamilyId,
    jobFamilyName: family?.jobFamilyName || "",
    code: family?.code || "",
    levelId: value.levelId,
    level,
  };
  markDirty("jobFamily");
};

const handleJobFamilyClear = () => {
  jobFamily.value = null;
  jobFamilyQuery.value = "";
  jobFamilyResults.value = [];
  jobFamilyDialogOpen.value = false;
  markDirty("jobFamily");
};

const openJobFamilyDialog = () => {
  jobFamilyDialogOpen.value = true;
};

const handleJobFamilyPickerAssign = ({ jobFamilyId, levelId }) => {
  handleJobFamilyUpdate({ jobFamilyId, levelId });
  jobFamilyDialogOpen.value = false;
};

const searchJobFamilies = async (query) => {
  jobFamilyQuery.value = query;
  if (props.jobFamilyItems?.length) {
    const q = (query || "").trim().toLowerCase();
    jobFamilyResults.value = q
      ? props.jobFamilyItems.filter((f) =>
          `${f.jobFamilyName || ""} ${f.code || ""}`.toLowerCase().includes(q)
        )
      : props.jobFamilyItems;
    return;
  }
  const accountId = getAccountId();
  if (!accountId) {
    jobFamilyResults.value = [];
    return;
  }
  jobFamilyLoading.value = true;
  try {
    jobFamilyResults.value = await loadJobFamilyPickerResults(accountId, query);
  } catch {
    jobFamilyResults.value = [];
  } finally {
    jobFamilyLoading.value = false;
  }
};

/** Rule 34: a country change re-defaults Currency from the country until the user touches Currency. */
const applyCountryCurrencyDefault = (previousCountry) => {
  const nextCountry = payRangeLocation.value?.countryCode ?? null;
  if (previousCountry === nextCountry || !nextCountry) return;
  if (currencyTouched.value || isSingleOperatingCurrency.value) return;
  selectedCurrency.value = enrichOperatingCurrency(
    resolveCurrencyForCountry({
      currencies: props.currencyItems,
      countryCode: nextCountry,
      countries: locationCountryOptions.value,
    })
  );
};

const updatePayRangeLocation = (location) => {
  const previousCountry = payRangeLocation.value?.countryCode ?? null;
  payRangeLocation.value = enrichPayRangeLocation(location);
  hydrateLocationFromRefs(payRangeLocation.value);
  applyCountryCurrencyDefault(previousCountry);
};

const onLocationSearch = async (payload) => {
  await handleLocationSearch(payload);
  emit("onLocationSearch", payload);
};

const isStepTwoValid = computed(() => {
  if (topRange.value === null || topRange.value === "" || topRange.value === undefined) {
    return false;
  }
  if (
    bottomRange.value === null ||
    bottomRange.value === "" ||
    bottomRange.value === undefined
  ) {
    return false;
  }
  const max = Number(topRange.value);
  const min = Number(bottomRange.value);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
  if (min >= max) return false;
  return isStepOneValid.value;
});

/** Step 2 ranges are required by create-draft (min_amount / max_amount). */
const canSaveDraft = computed(
  () => isStepTwoValid.value && !internalSaving.value
);

const handleWorkModeChange = (item) => {
  selectedWorkMode.value = item;
  markDirty("workMode");
};

const handlePayBasisChange = (item) => {
  selectedPayBasis.value = item;
  markDirty("payBasis");
};

const handleCurrencyChange = (item) => {
  selectedCurrency.value = enrichOperatingCurrency(item);
  currencyTouched.value = true;
  markDirty("operatingCurrency");
};

const clearWorkMode = () => {
  selectedWorkMode.value = findWorkMode("UNSPECIFIED");
  markDirty("workMode");
};

const clearPayBasis = () => {
  selectedPayBasis.value = null;
  markDirty("payBasis");
};

const clearSelectedCurrency = () => {
  selectedCurrency.value = null;
  currencyTouched.value = true;
  markDirty("operatingCurrency");
};

const handleTopRangeChange = (value) => {
  topRange.value = value;
  markDirty("maxAmount");
  showRangeEmptyError.value = true;
};

const handleBottomRangeChange = (value) => {
  bottomRange.value = value;
  markDirty("minAmount");
  showRangeEmptyError.value = true;
};

const goBack = () => {
  localStepNumber.value = 1;
};

const goToStepTwo = () => {
  if (!isStepOneValid.value) return;
  localStepNumber.value = 2;
  showRangeEmptyError.value =
    topRange.value === null || bottomRange.value === null;
};

const dialogMaxWidth = computed(() => {
  if (suggestPanelOpen.value) {
    return "1200px";
  }
  return localStepNumber.value === 1 ? "974px" : "600px";
});

// --- Suggest Range panel --------------------------------------------------

/** Figma suggest header — line 1: code • job family › level • Paid in USD • Annually. */
const suggestHeaderPrimaryLine = computed(() => {
  const parts = [];
  const code = String(props.initialValues?.code || "").trim();
  if (code) parts.push(code);
  if (jobFamilyBreadcrumb.value) parts.push(jobFamilyBreadcrumb.value);
  if (selectedCurrency.value?.code) {
    parts.push(`Paid in ${selectedCurrency.value.code}`);
  } else if (selectedCurrency.value?.name) {
    parts.push(`Paid in ${selectedCurrency.value.name}`);
  }
  if (selectedPayBasis.value?.name) parts.push(selectedPayBasis.value.name);
  return parts.join(" • ");
});

/** Figma suggest header — line 2: work mode • country • region • sub-region. */
const suggestHeaderSecondaryLine = computed(() => {
  const parts = [];
  const workMode = selectedWorkMode.value;
  if (workMode?.name && String(workMode.code || "").toUpperCase() !== "UNSPECIFIED") {
    parts.push(workMode.name);
  }
  const path = buildLocationPath(
    payRangeLocation.value,
    locationCountryOptions.value || []
  );
  path.forEach((segment) => {
    if (String(segment || "").trim()) parts.push(segment);
  });
  return parts.join(" • ");
});

const buildSuggestSeed = () => toSuggestApiRequest(buildPayload());

const applySuggestResult = (response) => {
  const payload = response?.data || response || {};
  const nextSources = Array.isArray(payload.sources) ? payload.sources : [];
  if (!nextSources.length) {
    suggestState.value = "cannot-source";
    return;
  }

  suggestSources.value = nextSources;
  const nextSuggestion = payload.suggestion || {};
  suggestion.value = {
    min: nextSuggestion.min ?? null,
    median: nextSuggestion.median ?? null,
    max: nextSuggestion.max ?? null,
  };
  suggestMin.value = suggestion.value.min != null ? suggestion.value.min : "";
  suggestMax.value = suggestion.value.max != null ? suggestion.value.max : "";
  suggestState.value = "sourced";
};

const SUGGEST_FIELD_LABELS = {
  sub_region_type: "Sub-region type",
  sub_region_id: "Sub-region",
  region_id: "State or region",
  country_code: "Country",
  scope_level: "Location scope",
  operating_country_id: "Operating country",
  job_family_level_id: "Job family level",
};

function formatSuggestFieldLabel(field) {
  if (!field) return "";
  const key = String(field).trim();
  return SUGGEST_FIELD_LABELS[key] || key.replace(/_/g, " ");
}

const SUGGEST_UNAVAILABLE_ERROR_CODE = "pay_range_suggest_unavailable";

/** A retrieval/gateway failure: its backend detail is internal text, never user copy. */
const isSuggestServerFailure = (err) =>
  err?.response?.status >= 500 ||
  err?.response?.data?.error === SUGGEST_UNAVAILABLE_ERROR_CODE;

/**
 * Pull a user-visible title + message for the red IQThinkingBanner from API
 * validation errors. Never returns backend detail for a 5xx / unavailable failure.
 * @returns {{ title: string, message: string } | null}
 */
function parseSuggestApiError(err) {
  const data = err?.response?.data;
  if (!data || typeof data !== "object" || isSuggestServerFailure(err)) return null;

  const reasonCode = String(data.error || "").toUpperCase();
  if (reasonCode === "PAY_RANGE_LOCATION_INVALID") {
    const field = data.details?.field || data.field;
    const detail =
      data.details?.detail || data.details?.message || data.detail || data.message;
    const label = formatSuggestFieldLabel(field);
    const message =
      label && detail ? `${label}: ${detail}` : detail || label || null;
    if (message) {
      return {
        title: "Couldn't validate this location",
        message,
      };
    }
  }

  if (reasonCode === "INVALID_SEED_CONTEXT") {
    const detail = data.details?.detail || data.detail;
    if (detail) {
      return {
        title: "Couldn't suggest pay range",
        message: String(detail),
      };
    }
  }

  if (typeof data.detail === "string" && data.detail.trim()) {
    return {
      title: "Couldn't suggest pay range",
      message: data.detail.trim(),
    };
  }

  const fieldKeys = Object.keys(data).filter(
    (k) => k !== "error" && k !== "details" && k !== "detail"
  );
  for (const key of fieldKeys) {
    const raw = data[key];
    const text = Array.isArray(raw) ? raw[0] : raw;
    if (text != null && String(text).trim()) {
      const label = formatSuggestFieldLabel(key);
      return {
        title: "Couldn't suggest pay range",
        message: `${label}: ${String(text).trim()}`,
      };
    }
  }

  return null;
}

/**
 * Map a failed suggest call to a failure state using the backend's
 * machine-readable reason (Rule 13):
 * - `timeout`   -> "timed-out" (13b) — only the backend's timeout reason.
 * - `no_sources` -> "cannot-source" (13a) — a completed search with nothing usable.
 * - a 4xx validation error with a user-safe message -> "cannot-source" shell
 *   carrying that message.
 * - anything else (unavailable, gateway / non-JSON 5xx, network error) ->
 *   "unavailable"; no backend text is shown.
 *
 * The streamed transport surfaces its terminal reason on `err.reason`; the HTTP
 * transport reports it as `response.data.reason`.
 */
const suggestErrorBanner = ref(null);

const resolveSuggestFailureState = (err) => {
  const data = err?.response?.data;
  const streamReason = typeof err?.reason === "string" ? err.reason : "";
  const dataReason = typeof data?.reason === "string" ? data.reason : "";
  const reason = streamReason || dataReason;
  suggestErrorBanner.value = null;
  saveError.value = "";
  if (reason === "timeout") return "timed-out";
  if (reason === "no_sources") return "cannot-source";
  const validationBanner = parseSuggestApiError(err);
  if (validationBanner) {
    suggestErrorBanner.value = validationBanner;
    return "cannot-source";
  }
  return "unavailable";
};

const beginSuggestSession = () => {
  suggestPanelOpen.value = true;
  suggestLoading.value = true;
  suggestState.value = "sourcing";
  suggestSources.value = [];
  suggestion.value = { min: null, median: null, max: null };
  suggestMin.value = null;
  suggestMax.value = null;
  suggestErrorBanner.value = null;
  suggestReasoning.value = "";
  suggestPhase.value = "";
  suggestStalled.value = false;
  suggestStartedAt.value = Date.now();
  suggestFinishedAt.value = null;
  saveError.value = "";
};

const runSuggest = async (seed) => {
  // A retry or a fresh panel always supersedes the previous stream.
  abortSuggestStream();
  const session = suggestSessionToken;
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  suggestAbortController = controller;
  const isCurrent = () => session === suggestSessionToken;

  try {
    if (typeof props.suggestRange === "function") {
      const response = await props.suggestRange(seed, {
        onThinking: (text) => {
          if (!isCurrent()) return;
          suggestReasoning.value = text || "";
          // New narrative means the silence watchdog no longer applies.
          suggestStalled.value = false;
        },
        onPhase: (phase) => {
          if (!isCurrent()) return;
          suggestPhase.value = phase || "";
          suggestStalled.value = false;
        },
        onStalled: () => {
          if (isCurrent()) suggestStalled.value = true;
        },
        signal: controller ? controller.signal : undefined,
      });
      if (!isCurrent()) return;
      suggestFinishedAt.value = Date.now();
      applySuggestResult(response);
    } else {
      if (!isCurrent()) return;
      suggestFinishedAt.value = Date.now();
      suggestState.value = "cannot-source";
    }
  } catch (err) {
    // An aborted stream is the user leaving or retrying — not a failed source.
    if (!isCurrent() || isSuggestAbortError(err)) return;
    suggestFinishedAt.value = Date.now();
    suggestState.value = resolveSuggestFailureState(err);
  } finally {
    if (isCurrent()) {
      suggestLoading.value = false;
      // Keep the controller reference: a later Try Again / close still aborts
      // the completed session's signal (a no-op for the transport).
    }
  }
};

const openSuggestPanel = async () => {
  if (!isStepOneValid.value) return;
  abortSuggestStream();
  await ensureLocationForSuggest();
  beginSuggestSession();

  const seed = buildSuggestSeed();
  emit("onSuggestRange", seed);
  await runSuggest(seed);
};

const retrySuggest = async () => {
  abortSuggestStream();
  await ensureLocationForSuggest();
  beginSuggestSession();

  // Same seed context, fresh call — the backend bypasses its retrieval cache.
  const seed = buildSuggestSeed();
  emit("onSuggestRange", seed);
  await runSuggest(seed);
};

const enterRangeManually = () => {
  // Step 1 values stay intact; Step 2 manual amounts start empty and no
  // suggest source records are attached to the eventual save.
  abortSuggestStream();
  suggestPanelOpen.value = false;
  suggestLoading.value = false;
  suggestState.value = "sourcing";
  suggestSources.value = [];
  suggestion.value = { min: null, median: null, max: null };
  suggestMin.value = null;
  suggestMax.value = null;
  suggestReasoning.value = "";
  suggestPhase.value = "";
  suggestStalled.value = false;
  suggestStartedAt.value = null;
  suggestFinishedAt.value = null;
  topRange.value = null;
  bottomRange.value = null;
  sources.value = null;
  localStepNumber.value = 2;
  showRangeEmptyError.value = true;
};

const closeSuggestPanel = () => {
  resetSuggestState();
  localStepNumber.value = 1;
};

const isSuggestFailure = computed(() =>
  ["cannot-source", "timed-out", "unavailable"].includes(suggestState.value)
);

const SUGGEST_FAILURE_COPY = {
  "timed-out": {
    title: "Sourcing timed out",
    message:
      "Market data sourcing took too long to respond. Try again, or enter the range manually.",
  },
  unavailable: {
    title: "Couldn't reach market data sources",
    message:
      "We couldn't reach the market data sources. Try again, or enter the range manually.",
  },
  "cannot-source": {
    title: "Couldn't source market data",
    message:
      "We couldn't retrieve pay data from any market source for this scope. Try again, or enter the range manually.",
  },
};

const suggestFailureCopy = computed(
  () => SUGGEST_FAILURE_COPY[suggestState.value] || SUGGEST_FAILURE_COPY["cannot-source"]
);

const suggestFailureTitle = computed(
  () => suggestErrorBanner.value?.title || suggestFailureCopy.value.title
);

const suggestFailureMessage = computed(
  () => suggestErrorBanner.value?.message || suggestFailureCopy.value.message
);

const handleSuggestMaxChange = (value) => {
  suggestMax.value = value;
};

const handleSuggestMinChange = (value) => {
  suggestMin.value = value;
};

const isValidRange = (min, max) => {
  if (min === null || min === "" || min === undefined) return false;
  if (max === null || max === "" || max === undefined) return false;
  const minN = Number(min);
  const maxN = Number(max);
  if (!Number.isFinite(minN) || !Number.isFinite(maxN)) return false;
  return minN < maxN;
};

const canSaveSuggest = computed(
  () =>
    !suggestLoading.value &&
    !internalSaving.value &&
    isValidRange(suggestMin.value, suggestMax.value)
);

const excludedSources = computed(() =>
  suggestSources.value.filter(isExcludedSource)
);

const hasSuggestion = computed(
  () =>
    suggestion.value.min != null ||
    suggestion.value.median != null ||
    suggestion.value.max != null
);

const isExcludedSource = (source) =>
  String(source?.status || "").toLowerCase() === "excluded";

const formatSourceAmount = (value) =>
  value == null || value === "" ? "—" : formatPayRangeAmount(value);

const buildLocationPayload = () => {
  const loc = payRangeLocation.value;
  if (!loc) return null;
  const operatingCountryId = resolveOperatingCountryId(
    loc,
    locationCountryOptions.value || [],
    props.countryItems || []
  );
  const operatingCountry = (props.countryItems || []).find(
    (c) =>
      String(c?.code || "").toUpperCase() ===
      String(loc.countryCode || "").toUpperCase()
  );
  return {
    ...loc,
    operatingCountryId,
    countryName: loc.countryName || operatingCountry?.name || "",
  };
};

const buildPayload = () => ({
  mode: resolvedMode.value,
  payRangeId: props.payRangeId,
  jobFamily: jobFamily.value,
  jobFamilyLevelId: jobFamilyLevelId.value,
  location: buildLocationPayload(),
  locationCountryOptions: locationCountryOptions.value || [],
  operatingCountryItems: props.countryItems || [],
  operatingCurrencyItems: props.currencyItems || [],
  workMode: selectedWorkMode.value?.code || "UNSPECIFIED",
  operatingCurrency: enrichOperatingCurrency(selectedCurrency.value),
  payBasis: selectedPayBasis.value?.code || "ANNUAL",
  minAmount: bottomRange.value,
  maxAmount: topRange.value,
  sources: sources.value || null,
  effectiveFrom: effectiveFrom.value,
  effectiveTo: effectiveTo.value,
});

const buildSuggestPayload = () => ({
  ...buildPayload(),
  minAmount: suggestMin.value,
  maxAmount: suggestMax.value,
  sources: suggestSources.value.length ? suggestSources.value : null,
});

const saveAsDraft = async () => {
  if (!canSaveDraft.value) return;
  saveError.value = "";
  isSaving.value = true;
  await ensureLocationForSuggest();
  const payload = buildPayload();

  try {
    emit("onSaveDraft", payload);
    if (typeof props.saveDraft === "function") {
      await props.saveDraft(payload);
    }
    isSaving.value = false;
    closeDialog(true);
  } catch (err) {
    saveError.value =
      err?.response?.data?.detail ||
      err?.response?.data?.error ||
      err?.message ||
      "Could not save draft. Please try again.";
    isSaving.value = false;
  }
};

const saveSuggestedRange = async () => {
  if (!canSaveSuggest.value) return;
  saveError.value = "";
  isSaving.value = true;
  await ensureLocationForSuggest();
  const payload = buildSuggestPayload();

  try {
    emit("onSaveDraft", payload);
    if (typeof props.saveDraft === "function") {
      await props.saveDraft(payload);
    }
    isSaving.value = false;
    closeDialog(true);
  } catch (err) {
    saveError.value =
      err?.response?.data?.detail ||
      err?.response?.data?.error ||
      err?.message ||
      "Could not save draft. Please try again.";
    isSaving.value = false;
  }
};

const closeDialog = (force = false) => {
  if (!force && internalSaving.value) return;
  // Dismissing the wizard cancels any in-flight sourcing stream.
  abortSuggestStream();
  emit("closeAddDialog");
  emit("onClose");
  emit("update:open", false);
};

const onDialogInput = (value) => {
  if (!value) closeDialog();
};

onMounted(async () => {
  loadCatalog();
  try {
    if (proxy && typeof proxy.$getFlag === "function") {
      const fairPayEnabled =
        (await proxy.$getFlag(LD_FLAG_IDEA_MDP_429, false)) === true;
      suggestRangeEnabled.value = fairPayEnabled;
      effectiveDatesEnabled.value = fairPayEnabled;
    }
  } catch {
    suggestRangeEnabled.value = false;
    effectiveDatesEnabled.value = false;
  }
});

defineExpose({
  jobFamily,
  jobFamilyLevelId,
  jobFamilyBreadcrumb,
  jobFamilyResults,
  jobFamilyLoading,
  jobFamilyQuery,
  payRangeLocation,
  payRangeLocationLabel,
  stepTwoMetaLabel,
  locationCountryOptions,
  hasStepOneContext,
  topRange,
  bottomRange,
  localStepNumber,
  isStepOneValid,
  stepOneButtonsDisabled,
  isStepTwoValid,
  canSaveDraft,
  canSaveSuggest,
  saveError,
  selectedCurrency,
  isSingleOperatingCurrency,
  pickerSessionKey,
  suggestPanelOpen,
  suggestLoading,
  suggestState,
  suggestSources,
  suggestReasoning,
  suggestPhase,
  suggestStalled,
  suggestStartedAt,
  suggestElapsedMs,
  suggestIsConnecting,
  abortSuggestStream,
  suggestRangeEnabled,
  effectiveDatesEnabled,
  effectiveFrom,
  effectiveTo,
  isEffectivePeriodValid,
  isSuggestFailure,
  goToStepTwo,
  goBack,
  closeDialog,
  saveAsDraft,
  saveSuggestedRange,
  openSuggestPanel,
  retrySuggest,
  enterRangeManually,
  closeSuggestPanel,
  buildPayload,
  buildSuggestPayload,
  buildSuggestSeed,
});
</script>

<style scoped>
.modal_header {
  display: flex;
  padding: 24px 24px 20px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  z-index: 1;
  position: relative;
}

.modal_header__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
}

.modal_header__content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.modal_header__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
}

.modal_header__title {
  margin: 0;
  width: 100%;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  text-align: center;
  color: #101828;
}

.text-black-600 {
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  color: rgba(16, 24, 40, 1);
}

.text-black-500 {
  display: block;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: rgba(52, 64, 84, 1);
}

.field-label {
  display: block;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: left;
  color: rgba(52, 64, 84, 1);
  margin: 0 0 8px;
}

.input {
  border-radius: 8px;
  margin: 0;
}

.pop-up {
  width: 500px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 1);
  position: relative;
  overflow: hidden;
  background-image: url("../assets/pay-range-editor-background.svg");
  max-width: 100%;
}

.pop-up--step-one {
  width: 974px;
}

.pop-up--step-two {
  width: 600px;
}

.pop-up--suggest {
  width: 1200px;
}

.pop-up--suggest-loading {
  width: 600px;
}

.modal_content {
  z-index: 1;
  position: relative;
}

.step-one-column--settings ::v-deep .list-picker-container,
.step-one-column--settings ::v-deep .res-custom-list-picker,
.step-one-column--settings ::v-deep .res-v-list-item {
  background-color: #ffffff;
}

.modal_content .custom-response-picker {
  width: 100% !important;
}

.checkbox-row {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  cursor: pointer;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.checkbox-box {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkbox-text-col {
  display: flex;
  flex-direction: column;
}

.checkbox-label-text {
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.checkbox-description {
  color: #475467;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

/* ER-11873: single-operating-currency helper text under the Used Currency field.
 * Matches the Figma "Helper text" node (3808:8940): #475467, Inter 400 14/20. */
.pay-range-currency-helper {
  margin: 6px 0 0;
  color: #475467;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

::v-deep(.expressed-selector) {
  left: 0px !important;
}

::v-deep(.container) {
  padding: 0px !important;
}

/* Match Name label spacing for all SelectPicker labels */
::v-deep(.label-name) {
  margin: 0 0 8px !important;
  padding: 0 !important;
  color: rgba(52, 64, 84, 1) !important;
  font-family: Inter !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
}

::v-deep(.select-picker) {
  padding: 0 !important;
  margin: 0 !important;
}

::v-deep(.select-picker .v-input) {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}

.step-two-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 24px 16px;
}

.step-two-header__line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.step-two-header__icon {
  flex-shrink: 0;
}

.step-two-header__breadcrumb {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.step-two-header__meta {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.step-two-header__location {
  color: #101828;
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.secondary-text {
  color: #666;
  text-align: center;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

::v-deep(.pay-range-header .pay-range-expressed) {
  margin-left: 0px !important;
}

::v-deep(.readiness) {
  text-align: start !important;
}

::v-deep .changed-field fieldset {
  border: 1px solid #7f56d9 !important;
  background: #f2edff !important;
}

/* ExSelectPicker Avatar flag (icon path) — match CountryPicker sizing */
::v-deep .pay-range-currency-picker .selected-currency-icon {
  width: 24px !important;
  height: 24px !important;
}

::v-deep .pay-range-currency-picker .v-input__prepend-inner {
  margin-top: 8px !important;
  align-self: center;
}

/* ExSelectPicker sets 8px; the pay-range modal flag icon needs 9px to sit centered */
::v-deep .pay-range-currency-picker .has-countries .v-input__prepend-inner {
  margin-top: 9px !important;
}

::v-deep .custom-response-picker {
  width: 100% !important;
}

::v-deep .list-picker-container {
  width: 100%;
}

.step-one-container {
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.step-one-container > .modal_content {
  padding: 0 24px;
}

.step-one-effective-dates {
  min-height: 40px;
}

.step-one-container > .modal_content .field-label {
  margin-bottom: 6px;
}

/* Step 1: Location (left) + Currency / Pay Basis / Work (right) — AddEditPayRangeDraft. */
.step-one-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  align-items: flex-start;
}

.step-one-column,
.step-one-column--right {
  width: auto;
  flex: none;
  min-width: 0;
}

.step-one-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.step-one-column--location {
  padding: 9px 28px;
}

.step-one-column--settings {
  padding: 9px 0;
  gap: 23px;
}

.step-one-column--settings > .modal_content {
  padding: 0 24px;
}

.step-one-location-field .field-label {
  margin-bottom: 6px;
}

.step-one-location-field ::v-deep .location-picker {
  width: 100%;
}

.pay-range-job-family-field {
  width: 100%;
}

.pay-range-job-family-field__picked,
.pay-range-job-family-field__trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px 10px 14px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 1px 1px rgba(16, 24, 40, 0.05);
  box-sizing: border-box;
  min-height: 44px;
  cursor: pointer;
  text-align: left;
  font: inherit;
}

.pay-range-job-family-field__trigger {
  color: #667085;
}

.pay-range-job-family-field__trigger-label {
  flex: 1;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
}

.pay-range-job-family-field__icon,
.pay-range-job-family-field__trigger-icon {
  flex-shrink: 0;
}

.pay-range-job-family-field__breadcrumb {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  gap: 6px;
}

.pay-range-job-family-field__ancestor {
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
}

.pay-range-job-family-field__sep {
  color: #d0d5dd;
  font-size: 14px;
  line-height: 20px;
}

.pay-range-job-family-field__leaf {
  color: #6941c6;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
}

.pay-range-job-family-field__code {
  flex-shrink: 0;
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 20px;
}

.pay-range-job-family-field__clear {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin: -4px;
  border-radius: 6px;
  cursor: pointer;
}

.pay-range-job-family-field__clear:hover {
  background: #f2f4f7;
}

/* Location field trigger: local stacking so nested popovers stay predictable. */
.step-one-column--location ::v-deep .fpf__trigger {
  position: relative;
  z-index: 1;
}

.step-one-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 32px 24px 24px;
  min-width: 0;
}

.step-one-footer__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* Figma AddEditPayRangeDraft — primary disabled Enter Range */
.step-one-footer__actions ::v-deep .step-one-btn.storybook-button--normal-disable {
  background-color: #e0e0e0 !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  color: #ffffff !important;
}

/* "Step N of 2" + two-dot progress control (bottom-left of both footers). */
.step-indicator {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
}

.step-indicator__label {
  color: #344054;
  font-family: Inter;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.step-indicator__icon {
  border-radius: 50%;
}

::v-deep .res-custom-list-picker {
  width: 100% !important;
}

/* Original Step 1 select alignment — do not override SelectPicker further */
::v-deep(.custom-response-picker.v-input input) {
  color: #101828;
  font-family: Inter;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  margin-bottom: 0px !important;
}

::v-deep .v-input__slot {
  height: 44px !important;
}

.step-two-container {
  padding: 0 24px;
}

.step-two-body {
  display: flex;
  flex-direction: column;
  padding: 0 13px;
}

.step-two-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  flex-wrap: nowrap;
}

.step-two-footer__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-two-footer .footer-btn {
  flex: 0 0 auto;
  align-self: center;
}

::v-deep .step-two-footer .footer-btn.storybook-button--normal,
::v-deep .step-two-footer .footer-btn.storybook-button--width-auto {
  width: fit-content !important;
  min-width: 0 !important;
  height: 40px !important;
}

/* Step 2 without Step 1 context — explicit invalid/empty state, not a broken editor. */
.step-two-invalid {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 24px;
  margin: 0 0 24px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #f9fafb;
}

.step-two-invalid__title {
  color: #101828;
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.step-two-invalid__message {
  color: #475467;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.step-two-invalid__btn {
  margin-top: 8px;
}

.save-error {
  color: #d92d20;
  font-family: Inter;
  font-size: 13px;
  text-align: right;
  margin: 0 0 16px;
}

/* --- Suggest Range panel ------------------------------------------------ */
.suggest-container {
  padding: 0 24px 24px;
}

.suggest-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  padding: 0 24px 20px;
  margin-bottom: 4px;
}

.suggest-header__icon-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.suggest-iq-badge {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.suggest-header__family-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.suggest-header__primary,
.suggest-header__secondary {
  margin: 0;
  max-width: 100%;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.suggest-header__secondary {
  color: #475467;
  font-weight: 400;
}

.suggest-body {
  display: flex;
  gap: 32px;
  align-items: stretch;
}

.suggest-body--loading {
  display: block;
}

.suggest-thinking {
  padding: 0 0 8px;
  width: 100%;
  box-sizing: border-box;
}

.suggest-thinking ::v-deep(.iq-thinking-banner--thinking) {
  min-height: 300px;
  padding: 20px 24px 24px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.suggest-thinking ::v-deep(.iqtb-reasoning) {
  flex: 1 1 auto;
  justify-content: center;
  gap: 14px;
  padding: 28px 0 32px;
}

.suggest-thinking ::v-deep(.iqtb-skeleton__line) {
  height: 10px;
}

.suggest-thinking ::v-deep(.iqtb-caption) {
  margin-top: auto;
  padding-top: 8px;
  font-style: italic;
}

.suggest-left {
  flex: 0 0 40%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 13px;
}

.suggest-left-caption {
  text-align: left;
}

.suggest-right {
  flex: 1 1 60%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.suggest-right--full {
  flex: 1 1 100%;
  width: 100%;
  padding: 0px;
  box-sizing: border-box;
}

.suggest-right--full ::v-deep(.iq-thinking-banner--failed) {
  width: 100%;
}

.market-data {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.market-data-title {
  color: #344054;
  font-family: Inter;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.04em;
  margin: 0;
}

.market-data-table {
  width: 100%;
  border-collapse: collapse;
  font-family: Inter;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.market-data-table th,
.market-data-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #eaecf0;
}

.market-data-table th {
  color: #475467;
  font-weight: 500;
  font-size: 12px;
}

.source-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-link {
  color: #7f56d9;
  text-decoration: none;
}

.source-link:hover {
  text-decoration: underline;
}

.source--excluded td {
  text-decoration: line-through;
  color: #98a2b3;
}

.source--excluded .source-link {
  color: #98a2b3;
}

.outlier-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #f7dddd;
  color: #d80027;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  text-decoration: none;
}

.triangulated {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.suggestion-boxes {
  display: flex;
  gap: 12px;
}

.suggestion-box {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
}

.suggestion-box--muted {
  background: #f9fafb;
  border-color: #eaecf0;
}

.suggestion-label {
  color: #475467;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.suggestion-value {
  color: #101828;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.suggestion-rationale {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  line-height: 18px;
  margin: 0;
}

.exclusion-banner {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  border: 1px solid #e9d7fc;
  border-radius: 8px;
  background: #f4ebff;
}

.exclusion-item {
  color: #475467;
  font-family: Inter;
  font-size: 13px;
  line-height: 18px;
}

.suggest-footer {
  padding-top: 8px;
}
</style>

<style>
.pop-up .v-input__slot {
  min-height: 44px !important;
}
</style>
